import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import api from './api';
import { setAccessToken, clearLegacyTokenStorage } from './authSession';
import { defaultHomePath } from './accessPolicy';

const styles = {
    container: { width: '400px', margin: '40px auto', fontFamily: 'sans-serif', border: '1px solid #ccc', padding: '20px 40px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    formGroup: { marginBottom: '15px' },
    label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
    input: { width: '100%', padding: '10px', boxSizing: 'border-box' },
    button: { width: '100%', padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
    error: { color: 'red', marginTop: '10px', textAlign: 'center' },
    rememberMe: { display: 'flex', alignItems: 'center', marginBottom: '15px', fontSize: '14px' },
    requestLink: { display: 'block', marginTop: '16px', textAlign: 'center', color: '#007bff', textDecoration: 'none' },
    forgotPasswordLink: { display: 'block', textAlign: 'right', marginTop: '6px', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline', width: '100%' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', padding: '25px', borderRadius: '8px', width: '360px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)' },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' },
    secondaryButton: { padding: '8px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    primaryButton: { padding: '8px 14px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }
};

function LoginPage({ theme }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const isDark = theme === 'dark';

  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/token/', {
        username,
        password,
        remember_me: rememberMe,
      });
      const { access } = response.data || {};
      clearLegacyTokenStorage();
      if (access) {
        setAccessToken(access);
        try {
          const decodedToken = jwtDecode(access);
          if (decodedToken && typeof decodedToken === 'object') {
            navigate(defaultHomePath({
              isStaff: decodedToken.is_staff === true,
              groups: Array.isArray(decodedToken.groups) ? decodedToken.groups : [],
            }));
            return;
          }
        } catch (decodeError) {
          console.error("Error decoding token:", decodeError);
        }
      }

      navigate('/');
    } catch (err) {
      setError('Invalid username or password.');
      console.error("Login error:", err);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');
    try {
      const response = await api.post('/password-reset-request/', { email: resetEmail });
      setResetMessage(response.data?.message || 'If that email exists, a reset link was sent.');
    } catch (err) {
      setResetError('Failed to send reset link. Please check the email and try again.');
      console.error("Password reset error:", err);
    }
  };



  return (
    <div style={styles.container}>
      <h2>Login</h2>

      {/* Security Notice Banner */}
      <div style={{
          backgroundColor: isDark ? '#1a2733' : '#e8f4fd',
          color: isDark ? '#90caf9' : '#0277bd',
          padding: '12px 16px',
          borderRadius: '6px',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '20px',
          borderLeft: `4px solid ${isDark ? '#64b5f6' : '#0277bd'}`,
          lineHeight: '1.4',
          textAlign: 'left'
      }}>
          <div style={{ fontSize: '16px', marginTop: '-2px' }}>🔒</div>
          <div>
              <strong>Security Policy:</strong> To protect system data, accounts that are inactive for 30 consecutive days are automatically deleted. Please log in regularly to maintain your access.
          </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
          />
          <button
            type="button"
            onClick={() => setShowResetModal(true)}
            style={styles.forgotPasswordLink}
          >
            Forgot Password?
          </button>
        </div>
        <div style={styles.rememberMe}>
            <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ marginRight: '10px' }}
            />
            <label htmlFor="rememberMe">Remember Me</label>
        </div>
        {error && <p style={styles.error}>{error}</p>}
        <button type="submit" style={styles.button}>Login</button>
      </form>
      <Link to="/request-account" style={styles.requestLink}>Request an account</Link>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={{ marginTop: 0 }}>Reset Password</h3>
            {resetMessage ? (
              <div>
                <p style={{ color: 'green', fontSize: '14px', marginBottom: '15px' }}>{resetMessage}</p>
                <button
                  type="button"
                  onClick={() => { setShowResetModal(false); setResetMessage(''); setResetEmail(''); }}
                  style={styles.primaryButton}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset}>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '15px' }}>
                  Enter your registered email address to receive a reset link.
                </p>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>
                {resetError && <p style={styles.error}>{resetError}</p>}
                <div style={styles.modalActions}>
                  <button
                    type="button"
                    onClick={() => { setShowResetModal(false); setResetError(''); setResetEmail(''); }}
                    style={styles.secondaryButton}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.primaryButton}>Send Link</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>

  );
}

export default LoginPage;